/**
 * The push notification categories of the noBGP app (Account page, inside the app only).
 *
 * ⚠ The ids are the router's (`push.Categories`, src/push/push.go, and the CHECK of
 * push_preferences.muted, migration 0253). A category the router answers and this list does not
 * know is not shown; one this list knows and the router does not answer is not shown either.
 */

export type PushCategoryId = 'node_status' | 'access_requests' | 'compute_deadlines' | 'billing'

export type PushCategory = { id: PushCategoryId; label: string; detail: string }

export const PUSH_CATEGORIES: readonly PushCategory[] = [
  {
    id: 'node_status',
    label: 'Node offline and back online',
    detail: 'When one of your nodes has been offline for 5 minutes, and when it reconnects.',
  },
  {
    id: 'access_requests',
    label: 'Access requests',
    detail: 'When someone asks to use one of your services or nodes.',
  },
  {
    id: 'compute_deadlines',
    // ⚠ The router sends these with the compute time limit email, to the people who turned that
    // email on (router#1552). The detail says so, or a switch that is on sends nothing.
    label: 'Compute time limit',
    detail: 'Sent with the time limit email: turn on "Email me before a machine reaches its time limit" below.',
  },
  {
    id: 'billing',
    label: 'Billing alerts',
    detail: 'Usage and credit alerts, to the Owners of an organization.',
  },
]

export type PushSetting = PushCategory & { enabled: boolean }

/** The router's answer, `{ categories: [{ id, enabled }] }`, as the rows the page shows, in order.
 * Anything that is not that shape is null: the page then says the settings could not be read. */
export const pushSettingsFrom = (answer: unknown): PushSetting[] | null => {
  const list = (answer as { categories?: unknown } | null)?.categories
  if (!Array.isArray(list)) return null
  const enabled = new Map<string, boolean>()
  for (const item of list) {
    const row = item as { id?: unknown; enabled?: unknown }
    if (typeof row?.id === 'string' && typeof row.enabled === 'boolean') enabled.set(row.id, row.enabled)
  }
  return PUSH_CATEGORIES.filter(c => enabled.has(c.id)).map(c => ({ ...c, enabled: enabled.get(c.id) === true }))
}

/** What the phone allows: @capacitor/push-notifications' `receive`. */
export type PushPermission = 'granted' | 'denied' | 'prompt' | 'unknown'

export const pushPermissionFrom = (answer: unknown): PushPermission => {
  const receive = (answer as { receive?: unknown } | null)?.receive
  if (receive === 'granted' || receive === 'denied') return receive
  if (receive === 'prompt' || receive === 'prompt-with-rationale') return 'prompt'
  return 'unknown'
}
